import React, { useMemo } from "react";
import type { DocumentChangeAuditEntity } from "../../../domain/entities/DocumentChangeAuditEntity";
import { buildInvoiceBillReviewChangesViewModel } from "../../../../../shared/utils/review";
import { useTaxes } from "../../../../../shared/presentation/hooks/useTaxes";
import { formatActionTypeLabel } from "../../../../../shared/utils/review/reviewActionLabels";

interface DocumentChangeDetailPanelProps {
  audit: DocumentChangeAuditEntity;
}

export const DocumentChangeDetailPanel: React.FC<
  DocumentChangeDetailPanelProps
> = ({ audit }) => {
  const { taxes } = useTaxes();
  const kind = audit.entityType === "bill" ? "bill" : "invoice";

  const viewModel = useMemo(
    () =>
      buildInvoiceBillReviewChangesViewModel({
        kind,
        changeDetails: audit.changeDetails,
        taxes,
        requiresReview: false,
        actionCode: audit.actionCode,
      }),
    [audit.actionCode, audit.changeDetails, kind, taxes],
  );

  const hasChanges = audit.hasMeaningfulChanges && !viewModel.isEmpty;

  return (
    <div className="flex w-full flex-col items-start gap-4">
      {audit.reason ? (
        <div className="flex w-full flex-col items-start gap-1 rounded-md bg-neutral-50 p-3">
          <span className="text-caption font-caption font-semibold text-subtext-color">
            Reason
          </span>
          <span className="text-body font-body text-default-font">
            {audit.reason}
          </span>
        </div>
      ) : null}

      {viewModel.actionTypes.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {viewModel.actionTypes.map((actionType) => (
            <span
              key={actionType}
              className="rounded-full bg-blue-100 px-2 py-0.5 text-caption font-caption text-blue-800"
            >
              {formatActionTypeLabel(actionType)}
            </span>
          ))}
        </div>
      ) : null}

      {!hasChanges ? (
        <div className="flex w-full flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
          <span className="text-body font-body font-semibold text-default-font">
            No manual edits detected
          </span>
          <span className="text-caption font-caption text-subtext-color">
            This document was posted without meaningful field changes compared to
            the extracted baseline.
          </span>
        </div>
      ) : null}

      {hasChanges && viewModel.headerChanges.length > 0 ? (
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-caption font-caption font-semibold text-subtext-color">
            Header changes
          </span>
          <ul className="flex w-full flex-col items-start gap-1">
            {viewModel.headerChanges.map((change) => (
              <li
                key={`${change.label}-${change.oldText}-${change.newText}`}
                className="text-body font-body text-default-font"
              >
                <span className="font-semibold">{change.label}:</span>{" "}
                <span className="text-red-600 line-through">
                  {change.oldText || "—"}
                </span>{" "}
                →{" "}
                <span className="text-green-700">{change.newText || "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hasChanges && viewModel.lineItemChanges.length > 0 ? (
        <div className="flex w-full flex-col items-start gap-3">
          <span className="text-caption font-caption font-semibold text-subtext-color">
            Line item changes
          </span>
          {viewModel.lineItemChanges.map((group) => (
            <div
              key={group.lineNumber}
              className="flex w-full flex-col items-start gap-1 rounded border border-solid border-neutral-200 bg-white p-3"
            >
              <span className="text-body font-body font-semibold text-default-font">
                {group.title}
              </span>
              <ul className="flex w-full flex-col items-start gap-1">
                {group.changes.map((change) => (
                  <li
                    key={`${group.lineNumber}-${change.label}`}
                    className="text-caption font-caption text-default-font"
                  >
                    <span className="font-semibold">{change.label}:</span>{" "}
                    <span className="text-red-600 line-through">
                      {change.oldText || "—"}
                    </span>{" "}
                    →{" "}
                    <span className="text-green-700">
                      {change.newText || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}

      {hasChanges && viewModel.glOverrideSections.length > 0 ? (
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-caption font-caption font-semibold text-subtext-color">
            GL overrides
          </span>
          {viewModel.glOverrideSections.map((group) => (
            <div
              key={group.lineNumber}
              className="flex w-full flex-col items-start gap-1 rounded border border-solid border-neutral-200 bg-white p-3"
            >
              <span className="text-body font-body font-semibold text-default-font">
                {group.title}
              </span>
              <span className="text-caption font-caption text-default-font">
                {group.accountChange.label}: {group.accountChange.oldText} →{" "}
                {group.accountChange.newText}
              </span>
              {group.reason ? (
                <span className="text-caption font-caption text-subtext-color">
                  Reason: {group.reason}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
